/**
 * The browser half of WebAuthn.
 *
 * The API speaks `ArrayBuffer`s while JSON speaks strings, so every id, challenge and
 * signature has to cross that line in both directions. Base64**url** specifically — the
 * standard alphabet's `+` and `/` are not URL-safe and are not what the server emits.
 *
 * Kept as plain functions rather than a hook: they are pure conversions, used by both the
 * profile page and the sign-in screen, and neither needs React to do them.
 */

/** True when this browser can do WebAuthn at all — old browsers and insecure origins cannot. */
export const isPasskeySupported = () =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials?.create;

export const base64UrlToBuffer = (value) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

export const bufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

/**
 * Runs the registration ceremony and returns what the server needs to verify it.
 *
 * `options` is the server's payload verbatim; only the fields the API insists are buffers
 * are converted, so a field the server starts sending tomorrow arrives untouched.
 */
export const createPasskey = async (options) => {
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials ?? []).map(
        (descriptor) => ({
          ...descriptor,
          id: base64UrlToBuffer(descriptor.id),
        })
      ),
    },
  });

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64Url(
        credential.response.attestationObject
      ),
      // Chrome exposes this, older Safari does not — an absent list is valid, a crash is not.
      transports: credential.response.getTransports?.() ?? [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
};

/** Runs the sign-in ceremony against the credentials the server says this account holds. */
export const getPasskeyAssertion = async (options) => {
  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials ?? []).map((descriptor) => ({
        ...descriptor,
        id: base64UrlToBuffer(descriptor.id),
      })),
    },
  });

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64Url(
        credential.response.authenticatorData
      ),
      signature: bufferToBase64Url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64Url(credential.response.userHandle)
        : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
};

/**
 * Turns a ceremony rejection into something worth showing.
 *
 * The browser reports a cancelled prompt and a genuine failure with the *same*
 * `NotAllowedError`, so it is treated as "the user changed their mind" — the common case by
 * far, and telling someone their device failed when they simply pressed Escape is worse than
 * saying nothing. Returning null means "say nothing".
 */
export const describePasskeyError = (error, t) => {
  if (!error) return null;

  if (error.name === "NotAllowedError" || error.name === "AbortError")
    return null;

  if (error.name === "InvalidStateError") {
    return t("profile.twoFactor.passkey.alreadyRegistered");
  }

  // The browser refuses before it ever asks the authenticator when the relying-party id
  // the server issued is not this page's domain — which happens when the API is served
  // from a different host than the app. It is a deployment fault, and "try again" is the
  // one piece of advice that cannot help, so it says so instead.
  if (error.name === "SecurityError") {
    return t("profile.twoFactor.passkey.misconfigured");
  }

  return t("profile.twoFactor.passkey.failed");
};
